import React, { useEffect, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import API from "../api";
import BackButton from "./common/BackButton";
import { SEMANTIC } from "../styles/theme";
import { useAppModal } from "../hooks/useAppModal";

const TERMINAL_STATUSES = ['filled', 'canceled', 'rejected', 'expired', 'timeout', 'error'];
const CANCELABLE_STATUSES = ['created', 'submitted', 'queued', 'new', 'partial', 'accepted', 'pending_new'];

// ======================================================
// Mr. K — drawer per spiegare i dati del portafoglio
// ======================================================
function buildPortfolioPrompt(p, orders) {
  if (!p) return "Spiegami i dati di questo portafoglio.";
  const fmt = (v, decimals = 2) => v != null ? Number(v).toFixed(decimals) : "n/d";
  const fmtPct = (v) => v != null ? `${Number(v) >= 0 ? "+" : ""}${Number(v).toFixed(2)}%` : "n/d";

  const TERMINAL = ['filled', 'canceled', 'rejected', 'expired', 'timeout', 'error'];
  const pendingOrders = (orders || []).filter(o => !TERMINAL.includes(o.status));
  const filledOrders  = (orders || []).filter(o => o.status === 'filled');

  const lines = [
    `Sto visualizzando i dati tecnici del portafoglio "${p.Nome_tipo_modello || "—"}".`,
    "",
    "**Stato attuale del portafoglio**",
    `- Liquidità: $${Number(p.liquidity || 0).toFixed(2)}`,
    `- Valore asset: $${Number(p.asset_value || 0).toFixed(2)}`,
  ];

  if (pendingOrders.length > 0) {
    lines.push(`- Ordini in attesa di esecuzione (mercato chiuso): ${pendingOrders.length} ordini pending`);
    pendingOrders.forEach(o => lines.push(`  • ${o.side?.toUpperCase()} ${o.quantity} ${o.symbol} — stato: ${o.status}`));
  } else if (filledOrders.length > 0) {
    lines.push(`- Ordini eseguiti: ${filledOrders.length}`);
  } else {
    lines.push(`- Nessun ordine presente`);
  }

  lines.push(
    "",
    "**Indicatori**",
    `- Alpha: ${p.alpha != null ? fmtPct(Number(p.alpha) * 100) : "n/d"}`,
    `- Beta: ${fmt(p.beta, 3)}`,
    `- Sharpe Ratio (overall): ${fmt(p.sharpe_ratio, 4)}`,
    `- Sharpe 3m: ${fmt(p.sharpe_ratio_3m, 4)}`,
    `- Sharpe 6m: ${fmt(p.sharpe_ratio_6m, 4)}`,
    `- Sharpe 12m: ${fmt(p.sharpe_ratio_12m, 4)}`,
    `- Sharpe 24m: ${fmt(p.sharpe_ratio_24m, 4)}`,
    "",
    "**Performance vs S&P 500**",
    `- 3 mesi: portafoglio ${fmtPct(p.perf_3m)}, differenza vs S&P500 ${fmtPct(p.sp500_diff_3m)}`,
    `- 6 mesi: portafoglio ${fmtPct(p.perf_6m)}, differenza vs S&P500 ${fmtPct(p.sp500_diff_6m)}`,
    `- 12 mesi: portafoglio ${fmtPct(p.perf_12m)}, differenza vs S&P500 ${fmtPct(p.sp500_diff_12m)}`,
    `- 24 mesi: portafoglio ${fmtPct(p.perf_24m)}, differenza vs S&P500 ${fmtPct(p.sp500_diff_24m)}`,
    "",
    `Puoi spiegarmi in modo semplice cosa significano questi valori e come interpretarli per valutare la qualità di questo portafoglio?`,
  );
  return lines.join("\n");
}

function MrKPortfolioDrawer({ open, onClose, portfolioData, orders }) {
  const userId = localStorage.getItem("userId");
  const [messages, setMessages] = React.useState([]);
  const [input, setInput] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [booted, setBooted] = React.useState(false);
  const bottomRef = React.useRef(null);

  React.useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  React.useEffect(() => {
    if (open && !booted) {
      setBooted(true);
      sendMessage(buildPortfolioPrompt(portfolioData, orders));
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendMessage = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: q }]);
    setLoading(true);
    try {
      const history = messages.filter(m => m.role === "user" || m.role === "assistant");
      const res = await API.post("/ai/advisor", { user_id: Number(userId), question: q, history });
      setMessages(prev => [...prev, { role: "assistant", content: res.data.answer || "" }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: "error", content: e.response?.data?.error || "Errore nella risposta AI" }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {open && <div onClick={onClose} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.55)", zIndex: 999 }} />}
      <div style={{
        position: "fixed", bottom: 0, left: 0, right: 0, height: "72vh",
        backgroundColor: SEMANTIC.backgroundCard,
        borderRadius: "20px 20px 0 0",
        boxShadow: "0 -8px 40px rgba(0,0,0,0.6)",
        zIndex: 1000, display: "flex", flexDirection: "column",
        transform: open ? "translateY(0)" : "translateY(100%)",
        transition: "transform 0.35s cubic-bezier(0.32,0.72,0,1)",
      }}>
        {/* Handle + header */}
        <div style={{ padding: "14px 20px 12px", borderBottom: "1px solid rgba(255,215,0,0.12)", flexShrink: 0 }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}>
            <div style={{ width: 36, height: 4, borderRadius: 2, backgroundColor: "rgba(255,255,255,0.15)" }} />
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 24 }}>🪐</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: "#ffd700" }}>Mr. K — AI Advisor</div>
                <div style={{ fontSize: 11, color: "#888" }}>Ti spiego i dati del tuo portafoglio</div>
              </div>
            </div>
            <button onClick={onClose} style={{ background: "none", border: "none", color: "#888", fontSize: 20, cursor: "pointer", padding: "4px 8px", lineHeight: 1 }}>✕</button>
          </div>
        </div>

        {/* Messaggi */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 16px 0", display: "flex", flexDirection: "column", gap: 12 }}>
          {messages.map((msg, i) => (
            <div key={i} style={{ display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start" }}>
              <div style={{
                maxWidth: "85%", padding: "10px 14px", fontSize: 14, lineHeight: 1.6, whiteSpace: "pre-wrap",
                borderRadius: msg.role === "user" ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                ...(msg.role === "user"
                  ? { backgroundColor: "rgba(255,215,0,0.13)", border: "1px solid rgba(255,215,0,0.28)", color: SEMANTIC.textPrimary }
                  : msg.role === "error"
                    ? { backgroundColor: "rgba(255,68,68,0.1)", border: "1px solid rgba(255,68,68,0.3)", color: "#ff6666" }
                    : { backgroundColor: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", color: SEMANTIC.textPrimary }),
              }}>
                {msg.role === "assistant" && <div style={{ fontSize: 10, color: "#ffd700", marginBottom: 6, fontWeight: 700 }}>🪐 Mr. K</div>}
                {msg.content}
              </div>
            </div>
          ))}
          {loading && (
            <div style={{ display: "flex", justifyContent: "flex-start" }}>
              <div style={{ padding: "10px 16px", borderRadius: "16px 16px 16px 4px", backgroundColor: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", display: "flex", gap: 5, alignItems: "center" }}>
                {[0, 1, 2].map(i => (
                  <div key={i} style={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: "#ffd700", animation: `mrk-bounce 1.2s ease-in-out ${i * 0.2}s infinite`, opacity: 0.7 }} />
                ))}
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div style={{ padding: "12px 16px", borderTop: "1px solid rgba(255,255,255,0.08)", backgroundColor: SEMANTIC.backgroundCard, display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
          <input
            type="text" value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && sendMessage()}
            placeholder="Scrivi un messaggio..." disabled={loading}
            style={{ flex: 1, padding: "10px 14px", borderRadius: 22, border: "1px solid rgba(255,215,0,0.25)", backgroundColor: "rgba(255,255,255,0.05)", color: SEMANTIC.textPrimary, fontSize: 14, outline: "none" }}
          />
          <button
            onClick={() => sendMessage()} disabled={loading || !input.trim()}
            style={{ width: 42, height: 42, borderRadius: "50%", border: "none", backgroundColor: loading || !input.trim() ? "rgba(255,215,0,0.2)" : "#ffd700", color: loading || !input.trim() ? "#888" : "#000", cursor: loading || !input.trim() ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}
          >
            <i className="bi bi-send-fill" />
          </button>
        </div>
      </div>
      <style>{`@keyframes mrk-bounce { 0%, 60%, 100% { transform: translateY(0); } 30% { transform: translateY(-6px); } }`}</style>
    </>
  );
}

const formatDateTime = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d);
  return dt.toLocaleString();
};

export default function PortfolioDetails() {
  const [portfolio, setPortfolio] = useState(null);
  const [plotExists, setPlotExists] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Zoom state
  const [scale, setScale] = useState(1);
  const [origin, setOrigin] = useState({ x: 0, y: 0 });
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const imgRef = useRef(null);
  const lastTouchDist = useRef(null);
  const lastPanPoint = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Orders / transactions state
  const [orders, setOrders] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [ordersTab, setOrdersTab] = useState("orders");
  const [cancelConfirm, setCancelConfirm] = useState(null);
  const pollingRef = useRef(null);

  const [mrkOpen, setMrkOpen] = useState(false);

  const portfolioId = localStorage.getItem("portfolioId");
  const { t } = useTranslation();
  const { modal, showModal } = useAppModal();

  // -------------------------------------------
  // FETCH ORDERS + TRANSACTIONS
  // -------------------------------------------
  const refreshOrdersTransactions = useCallback(async () => {
    if (!portfolioId) return;
    try {
      const [oRes, tRes] = await Promise.all([
        API.get(`/portfolio/${portfolioId}/orders`),
        API.get(`/portfolio/${portfolioId}/transactions`),
      ]);
      setOrders(oRes.data?.orders || []);
      setTransactions(tRes.data?.transactions || []);
    } catch {}
  }, [portfolioId]);

  useEffect(() => {
    refreshOrdersTransactions();
  }, [refreshOrdersTransactions]);

  // Auto-refresh open orders every 5 s
  useEffect(() => {
    if (ordersTab !== "orders") return;
    pollingRef.current = setInterval(async () => {
      try {
        const res = await API.get(`/portfolio/${portfolioId}/orders`);
        const newOrders = res.data?.orders || [];
        setOrders(newOrders);
        if (!newOrders.some(o => !TERMINAL_STATUSES.includes(o.status))) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      } catch {}
    }, 5000);
    return () => { if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; } };
  }, [portfolioId, ordersTab]);

  const onCancelOrder = async (id) => {
    try {
      await API.post(`/orders/${id}/cancel`);
      setCancelConfirm(null);
      refreshOrdersTransactions();
    } catch {
      setCancelConfirm(null);
    }
  };

  // -------------------------------------------
  // FETCH PORTFOLIO DETAILS
  // -------------------------------------------
  useEffect(() => {
    const fetchPortfolioDetails = async () => {
      try {
        const res = await axios.get(`/api/portfolio/details/${portfolioId}`);
        setPortfolio(res.data.portfolio);
      } catch (err) {
        console.error("❌ Error fetching portfolio details:", err);
        setError("Failed to fetch portfolio details.");
      } finally {
        setLoading(false);
      }
    };

    fetchPortfolioDetails();
  }, [portfolioId]);

  // -------------------------------------------
  // CHECK IF MODEL PLOT EXISTS
  // -------------------------------------------
  useEffect(() => {
    if (!portfolio?.model_id) return;

    const img = new Image();
    const path = `/api/simulation-plot/${portfolio.model_id}`;

    img.onload = () => setPlotExists(true);
    img.onerror = () => setPlotExists(false);
    img.src = path;
  }, [portfolio]);

  const modelId = portfolio?.model_id;
  const plotPath = `/api/simulation-plot/${modelId}`;

  // -------------------------------------------
  // FULLSCREEN ESC TO CLOSE
  // -------------------------------------------
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        setIsFullscreen(false);
        resetZoom();
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  // -------------------------------------------
  // PINCH-TO-ZOOM HANDLERS
  // -------------------------------------------
  const resetZoom = () => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
    lastTouchDist.current = null;
    lastPanPoint.current = null;
  };

  const getDistance = (touch1, touch2) => {
    return Math.sqrt(
      Math.pow(touch1.clientX - touch2.clientX, 2) +
      Math.pow(touch1.clientY - touch2.clientY, 2)
    );
  };

  const handleTouchStart = (e) => {
    if (!imgRef.current) return;

    if (e.touches.length === 2) {
      const dist = getDistance(e.touches[0], e.touches[1]);
      lastTouchDist.current = dist;

      const rect = imgRef.current.getBoundingClientRect();
      setOrigin({
        x: (e.touches[0].clientX + e.touches[1].clientX) / 2 - rect.left,
        y: (e.touches[0].clientY + e.touches[1].clientY) / 2 - rect.top,
      });
    }

    if (e.touches.length === 1) {
      lastPanPoint.current = {
        x: e.touches[0].clientX - offset.x,
        y: e.touches[0].clientY - offset.y,
      };
    }
  };

  const handleTouchMove = (e) => {
    if (!imgRef.current) return;

    if (e.touches.length === 2) {
      const currentDist = getDistance(e.touches[0], e.touches[1]);
      const delta = currentDist - lastTouchDist.current;

      let newScale = scale + delta * 0.003;
      newScale = Math.max(1, Math.min(newScale, 4));

      setScale(newScale);
      lastTouchDist.current = currentDist;
    }

    if (e.touches.length === 1 && scale > 1) {
      const newOffset = {
        x: e.touches[0].clientX - lastPanPoint.current.x,
        y: e.touches[0].clientY - lastPanPoint.current.y,
      };
      setOffset(newOffset);
    }
  };

  const handleTouchEnd = () => {
    if (scale <= 1) resetZoom();
  };

  let lastTap = 0;
  const handleDoubleTap = () => {
    const now = Date.now();
    if (now - lastTap < 300) {
      if (scale === 1) setScale(2);
      else resetZoom();
    }
    lastTap = now;
  };


  // -------------------------------------------
  // RENDER
  // -------------------------------------------

  if (loading) {
    return (
      <div style={{
        backgroundColor: SEMANTIC.backgroundApp,
        color: SEMANTIC.textPrimary,
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "20px",
      }}>
        {t("portfolio_details.loading")}
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        backgroundColor: SEMANTIC.backgroundApp,
        color: SEMANTIC.textPrimary,
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "18px",
      }}>
        {error}
      </div>
    );
  }

  if (!portfolio) {
    return (
      <div style={{
        backgroundColor: SEMANTIC.backgroundApp,
        color: SEMANTIC.textPrimary,
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}>
        <p>{t("portfolio_details.not_found")}</p>
      </div>
    );
  }

  // ANIMATIONS
  const fadeIn = { animation: "fadeIn 0.7s ease-out" };
  const slideIn = { animation: "slideIn 0.6s ease-out" };

  const indicatorLabels = [
    ["alpha",           t("portfolio_details.param_alpha"),  "alpha"],
    ["beta",            t("portfolio_details.param_beta"),   "beta"],
    ["sharpe_ratio",    "Sharpe Ratio",                      "sharpe"],
    ["sharpe_ratio_3m", "Sharpe 3m",                         "sharpe"],
    ["sharpe_ratio_6m", "Sharpe 6m",                         "sharpe"],
    ["sharpe_ratio_12m","Sharpe 12m",                        "sharpe"],
    ["sharpe_ratio_24m","Sharpe 24m",                        "sharpe"],
    ["sharpe_ratio_36m","Sharpe 36m",                        "sharpe"],
  ];

  const perfPeriods = [
    [t("portfolio_details.perf_3m"), "perf_3m", "sp500_diff_3m"],
    [t("portfolio_details.perf_6m"), "perf_6m", "sp500_diff_6m"],
    [t("portfolio_details.perf_12m"), "perf_12m", "sp500_diff_12m"],
    [t("portfolio_details.perf_24m"), "perf_24m", "sp500_diff_24m"],
  ];

  return (
    <>
    {modal}
    <div style={{
      backgroundColor: SEMANTIC.backgroundApp,
      color: SEMANTIC.textPrimary,
      minHeight: "100vh",
      padding: "20px",
    }}>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideIn { from { transform: translateY(15px); opacity:0; } to { transform: translateY(0); opacity:1; } }
        @keyframes zoomIn { from { transform: scale(0.85); opacity: 0; } to { transform: scale(1); opacity: 1; } }
      `}</style>

      {/* BACK BUTTON */}
      <BackButton to="/portfolio" />

      {/* HEADER TILE */}
      <div style={{
        backgroundColor: SEMANTIC.backgroundCard,
        padding: "25px",
        borderRadius: "12px",
        marginBottom: "25px",
        boxShadow: "0 4px 8px rgba(0,0,0,0.7)",
        ...slideIn,
      }}>
        {/* Nome + asset type badge */}
        {(() => {
          const ASSET_CFG = {
            STOCK:  { label: 'Stocks',  color: '#00aaff', icon: 'bi-bar-chart-fill' },
            ETF:    { label: 'ETF',     color: '#ffaa00', icon: 'bi-collection-fill' },
            BOND:   { label: 'Bond',    color: '#00ff88', icon: 'bi-shield-fill' },
            CRYPTO: { label: 'Crypto',  color: '#aa00ff', icon: 'bi-currency-bitcoin' },
            ALL:    { label: 'Multi',   color: '#889aaa', icon: 'bi-grid-fill' },
          };
          const cfg = ASSET_CFG[(portfolio.ticker_universe || 'ALL').toUpperCase()] || ASSET_CFG.ALL;
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <h3 style={{ color: SEMANTIC.actionBuy, margin: 0 }}>{portfolio.Nome_tipo_modello}</h3>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '4px 12px', borderRadius: 20,
                fontSize: 12, fontWeight: 700, letterSpacing: 0.4,
                backgroundColor: `${cfg.color}18`, color: cfg.color,
                border: `1px solid ${cfg.color}44`,
              }}>
                <i className={`bi ${cfg.icon}`} style={{ fontSize: 12 }} />{cfg.label}
              </span>
            </div>
          );
        })()}
        <p style={{ color: SEMANTIC.textSecondary }}>{portfolio.Descrizione_tipo_modello}</p>
      </div>

      {/* MR. K BUTTON */}
      <div style={{ marginBottom: 12, textAlign: "center" }}>
        <button
          onClick={() => setMrkOpen(true)}
          style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            padding: "9px 20px", borderRadius: 24,
            backgroundColor: "rgba(255,215,0,0.08)",
            border: "1px solid rgba(255,215,0,0.35)",
            color: "#ffd700", fontSize: 13, fontWeight: 600,
            cursor: "pointer", whiteSpace: "nowrap",
            transition: "background-color 0.2s",
          }}
          onMouseOver={e => e.currentTarget.style.backgroundColor = "rgba(255,215,0,0.15)"}
          onMouseOut={e => e.currentTarget.style.backgroundColor = "rgba(255,215,0,0.08)"}
        >
          <span style={{ fontSize: 16 }}>🪐</span>
          Cosa sono questi dati? Chiedi a Mr. K
        </button>
      </div>

      {/* MODEL TILE */}
      <div style={{
        backgroundColor: SEMANTIC.backgroundCard,
        padding: "20px",
        borderRadius: "12px",
        marginBottom: "25px",
        boxShadow: "0 4px 8px rgba(0,0,0,0.7)",
        ...fadeIn,
      }}>

        {/* SUBTILE 1 — RESULTS */}
        <div style={{
          backgroundColor: "#111",
          padding: "20px",
          borderRadius: "10px",
          boxShadow: "0 3px 6px rgba(0,0,0,0.5)",
        }}>
          <h4 style={{ color: SEMANTIC.actionBuy }}>Performance</h4>

          {(() => {
            const scaleToFive = (v) => {
              if (v == null) return 1;
              const val = Math.max(0, Math.min(1, parseFloat(v)));
              return Math.max(1, Math.min(5, Math.round(val * 4) + 1));
            };
            const computeIntervallo = (p) => {
              const diffs = [
                [1, p.sp500_diff_3m],
                [2, p.sp500_diff_6m],
                [3, p.sp500_diff_12m],
                [4, p.sp500_diff_24m],
                [5, p.sp500_diff_36m],
              ].filter(([, v]) => v != null);
              if (!diffs.length) return 1;
              return diffs.reduce((best, cur) => parseFloat(cur[1]) > parseFloat(best[1]) ? cur : best)[0];
            };
            const intervalloLabels = { 1: "3 mesi", 2: "6 mesi", 3: "12 mesi", 4: "24 mesi", 5: "36+ mesi" };
            const ScaleDots = ({ value, max, color }) => (
              <div style={{ display: "flex", gap: "5px", marginTop: "4px" }}>
                {Array.from({ length: max }, (_, i) => (
                  <div key={i} style={{
                    width: "12px", height: "12px", borderRadius: "50%",
                    backgroundColor: i < value ? color : "#444",
                    border: `1px solid ${color}`,
                  }} />
                ))}
              </div>
            );
            const ampVal = scaleToFive(portfolio.marketAmplification);
            const resVal = scaleToFive(portfolio.marketResistance);
            const intVal = computeIntervallo(portfolio);
            const InfoBtn = ({ titleKey, msgKey }) => (
              <button
                onClick={() => showModal({ type: 'info', title: t(titleKey), message: t(msgKey) })}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px', color: '#667', fontSize: '14px', lineHeight: 1 }}
                title={t('common.more_info')}
              >
                <i className="bi bi-info-circle" />
              </button>
            );
            return (
              <div style={{ display: "flex", flexDirection: "column", gap: "14px", marginBottom: "16px" }}>
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "13px", color: "#aaa" }}>
                      {t('portfolio_details.amplification')}
                      <InfoBtn titleKey="portfolio_details.amplification" msgKey="portfolio_details.info_amplification" />
                    </span>
                    <span style={{ fontSize: "13px", color: "#00aaff", fontWeight: "bold" }}>{ampVal}/5</span>
                  </div>
                  <ScaleDots value={ampVal} max={5} color="#00aaff" />
                </div>
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "13px", color: "#aaa" }}>
                      {t('portfolio_details.resistance')}
                      <InfoBtn titleKey="portfolio_details.resistance" msgKey="portfolio_details.info_resistance" />
                    </span>
                    <span style={{ fontSize: "13px", color: "#00ff88", fontWeight: "bold" }}>{resVal}/5</span>
                  </div>
                  <ScaleDots value={resVal} max={5} color="#00ff88" />
                </div>
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "13px", color: "#aaa" }}>
                      {t('portfolio_details.time_horizon')}
                      <InfoBtn titleKey="portfolio_details.time_horizon" msgKey="portfolio_details.info_time_horizon" />
                    </span>
                    <span style={{ fontSize: "13px", color: "#ffaa00", fontWeight: "bold" }}>{intervalloLabels[intVal]}</span>
                  </div>
                  <ScaleDots value={intVal} max={4} color="#ffaa00" />
                </div>
              </div>
            );
          })()}

          <div style={{
            marginTop: "15px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "10px",
          }}>
            {perfPeriods.map(([label, perfKey, diffKey]) => {
            const perfRaw = portfolio[perfKey];
            const diffRaw = portfolio[diffKey];

            const perf = perfRaw !== null && perfRaw !== undefined ? Number(perfRaw) : null;
            const diff = diffRaw !== null && diffRaw !== undefined ? Number(diffRaw) : null;

              const perfColor =
                typeof perf === "number"
                  ? perf >= 0 ? SEMANTIC.actionBuy : SEMANTIC.actionSell
                  : "#777";

              const diffColor =
                typeof diff === "number"
                  ? diff >= 0 ? SEMANTIC.actionBuy : SEMANTIC.actionSell
                  : "#777";

              return (
                <div
                  key={label}
                  style={{
                    backgroundColor: SEMANTIC.backgroundCard,
                    padding: "14x",
                    borderRadius: "8px",
                    border: "1px solid #333",
                  }}
                >
                  <p style={{ margin: 0, color: "#00aaff", fontSize: "15px" }}>
                    <strong>{label}</strong>
                  </p>

                  <p style={{ margin: 0 }}>
                    <span style={{ color: "#aaa", fontSize: "13px" }}>
                      {t("portfolio_details.performance")}{" "}
                    </span>

                    <span style={{ fontSize: "12px", color: perfColor }}>
                      <strong>
                        {typeof diff === "number"
                          ? `${diff > 0 ? "+" : ""}${perf.toFixed(2)}%`
                          : "N/A"}
                      </strong>
                    </span>
                  </p>

                  <p style={{ margin: 0 }}>
                    <span style={{ color: "#aaa", fontSize: "13px" }}>
                      {t("portfolio_details.vs_sp500")}{" "}
                    </span>

                    <span style={{ fontSize: "12px", color: diffColor }}>
                      <strong>
                        {typeof diff === "number"
                          ? `${diff > 0 ? "+" : ""}${diff.toFixed(2)}%`
                          : "N/A"}
                      </strong>
                    </span>
                  </p>

                </div>
              );
            })}
          </div>

          {plotExists ? (
            <img
              src={plotPath}
              alt="model simulation plot"
              onClick={() => {
                setIsFullscreen(true);
                resetZoom();
              }}
              style={{
                width: "100%",
                marginTop: "12px",
                borderRadius: "10px",
                border: "1px solid #444",
                cursor: "zoom-in",
                transition: "transform 0.2s ease",
              }}
            />
          ) : (
            <p style={{ color: "#777", marginTop: "10px" }}>
              {t("portfolio_details.no_plot")}
            </p>
          )}
        </div>

        {/* SUBTILE 2 — INDICATORI */}
        <div style={{
          backgroundColor: "#111",
          padding: "20px",
          borderRadius: "10px",
          marginTop: "22px",
          boxShadow: "0 3px 6px rgba(0,0,0,0.5)",
        }}>
          <h4 style={{ color: SEMANTIC.actionBuy }}>Indicatori</h4>

          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            {indicatorLabels.map(([key, label, type]) => {
              const raw = portfolio[key];
              if (raw == null) return null;
              const val = parseFloat(raw);

              let display, color;
              if (type === "alpha") {
                display = `${(val * 100).toFixed(2)}%`;
                color = val > 0 ? "#00ff88" : "#ff5555";
              } else if (type === "beta") {
                display = val.toFixed(3);
                color = val < 0.8 ? "#00ff88" : val <= 1.2 ? "#ffaa00" : "#ff5555";
              } else {
                // sharpe
                display = val.toFixed(4);
                color = val >= 1.5 ? "#00ff88" : val >= 0.5 ? "#ffaa00" : val >= 0 ? "#889" : "#ff5555";
              }

              return (
                <div key={key} style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: "#aab", fontSize: 13 }}>{label}</span>
                  <span style={{ color, fontSize: 13, fontWeight: 700 }}>{display}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* FULLSCREEN OVERLAY */}
      {isFullscreen && (
        <div
          onClick={() => {
            setIsFullscreen(false);
            resetZoom();
          }}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.85)",
            backdropFilter: "blur(2px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            cursor: "zoom-out",
            animation: "fadeIn 0.2s ease-out",
          }}
        >
          <img
            ref={imgRef}
            src={plotPath}
            alt="plot fullscreen"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchEnd}
            onDoubleClick={handleDoubleTap}
            style={{
              maxWidth: "95vw",
              maxHeight: "90vh",
              transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
              transformOrigin: `${origin.x}px ${origin.y}px`,
              transition: "transform 0.05s linear",
              borderRadius: "12px",
              boxShadow: "0 0 20px rgba(0,0,0,0.6)",
            }}
          />
        </div>
      )}

      {/* ORDERS & TRANSACTIONS TILE */}
      <div style={{
        backgroundColor: SEMANTIC.backgroundCard,
        padding: "20px",
        borderRadius: "10px",
        marginTop: "25px",
        boxShadow: "0 4px 8px rgba(0,0,0,0.7)",
        ...slideIn,
      }}>
        {/* Tabs */}
        <div style={{ display: "flex", gap: 24, marginBottom: 16, borderBottom: "1px solid #333", paddingBottom: 8 }}>
          {["orders", "transactions"].map(tab => (
            <span
              key={tab}
              onClick={() => setOrdersTab(tab)}
              style={{
                cursor: "pointer",
                fontWeight: "bold",
                fontSize: 14,
                color: ordersTab === tab ? SEMANTIC.actionBuy : SEMANTIC.textSecondary,
                borderBottom: ordersTab === tab ? `2px solid ${SEMANTIC.actionBuy}` : "none",
                paddingBottom: 4,
              }}
            >
              {tab === "orders" ? "Ordini" : "Transazioni"}
            </span>
          ))}
        </div>

        {ordersTab === "orders" ? (
          orders.length === 0 ? (
            <p style={{ color: "#666", textAlign: "center", margin: 0 }}>Nessun ordine</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ color: "#888", textAlign: "left" }}>
                    <th style={{ padding: "4px 8px" }}>Data</th>
                    <th style={{ padding: "4px 8px" }}>Ticker</th>
                    <th style={{ padding: "4px 8px" }}>Lato</th>
                    <th style={{ padding: "4px 8px" }}>Qtà</th>
                    <th style={{ padding: "4px 8px" }}>Stato</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(o => (
                    <tr key={o.internal_order_id} style={{ borderTop: "1px solid #2a2a2a" }}>
                      <td style={{ padding: "6px 8px", color: SEMANTIC.textSecondary }}>{formatDateTime(o.created_at)}</td>
                      <td style={{ padding: "6px 8px", fontWeight: "bold" }}>{o.symbol}</td>
                      <td style={{ padding: "6px 8px", color: o.side === "buy" ? SEMANTIC.actionBuy : SEMANTIC.actionSell }}>{o.side}</td>
                      <td style={{ padding: "6px 8px" }}>{o.quantity}</td>
                      <td style={{ padding: "6px 8px" }}>
                        <span style={{
                          display: "inline-block",
                          padding: "2px 8px",
                          borderRadius: 10,
                          fontSize: 11,
                          fontWeight: 600,
                          ...(o.status === "filled"
                            ? { color: "#4caf50", background: "rgba(76,175,80,0.15)", border: "1px solid rgba(76,175,80,0.35)" }
                            : o.status === "error"
                            ? { color: "#f44336", background: "rgba(244,67,54,0.15)", border: "1px solid rgba(244,67,54,0.35)" }
                            : { color: "#ffd700", background: "rgba(255,215,0,0.12)", border: "1px solid rgba(255,215,0,0.3)" })
                        }}>{o.status}</span>
                      </td>
                      <td style={{ padding: "6px 8px", textAlign: "center" }}>
                        {CANCELABLE_STATUSES.includes(o.status) && (
                          <button
                            onClick={() => setCancelConfirm({ id: o.internal_order_id, symbol: o.symbol })}
                            style={{
                              background: "transparent", border: "1px solid #ff6b6b",
                              color: "#ff6b6b", borderRadius: "50%",
                              width: 24, height: 24, fontSize: 12, cursor: "pointer",
                              display: "inline-flex", alignItems: "center", justifyContent: "center",
                            }}
                          >✕</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : (
          transactions.length === 0 ? (
            <p style={{ color: "#666", textAlign: "center", margin: 0 }}>Nessuna transazione</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ color: "#888", textAlign: "left" }}>
                    <th style={{ padding: "4px 8px" }}>Data</th>
                    <th style={{ padding: "4px 8px" }}>Tipo</th>
                    <th style={{ padding: "4px 8px" }}>Ticker</th>
                    <th style={{ padding: "4px 8px" }}>Qtà</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tr, i) => (
                    <tr key={i} style={{ borderTop: "1px solid #2a2a2a" }}>
                      <td style={{ padding: "6px 8px", color: SEMANTIC.textSecondary }}>{formatDateTime(tr.created_at)}</td>
                      <td style={{ padding: "6px 8px" }}>{tr.action_type}</td>
                      <td style={{ padding: "6px 8px", fontWeight: "bold" }}>{tr.stock_name}</td>
                      <td style={{ padding: "6px 8px" }}>{tr.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      {/* CANCEL ORDER DIALOG */}
      {cancelConfirm && (
        <div style={{
          position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.7)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: SEMANTIC.backgroundCard, borderRadius: "12px",
            padding: "32px 40px", textAlign: "center", minWidth: 300,
            boxShadow: "0 8px 24px rgba(0,0,0,0.8)",
          }}>
            <h4 style={{ marginBottom: 12 }}>Annulla ordine</h4>
            <p style={{ color: SEMANTIC.textSecondary, marginBottom: 20 }}>
              Sei sicuro di voler annullare l'ordine su <strong style={{ color: SEMANTIC.actionSell }}>{cancelConfirm.symbol}</strong>?
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <button
                onClick={() => setCancelConfirm(null)}
                style={{ padding: "10px 24px", borderRadius: "50px", border: "none", background: "#333", color: SEMANTIC.textPrimary, fontWeight: "bold", cursor: "pointer" }}
              >No, mantieni</button>
              <button
                onClick={() => onCancelOrder(cancelConfirm.id)}
                style={{ padding: "10px 24px", borderRadius: "50px", border: "none", background: SEMANTIC.actionSell, color: "#fff", fontWeight: "bold", cursor: "pointer" }}
              >Sì, annulla</button>
            </div>
          </div>
        </div>
      )}
    </div>

    <MrKPortfolioDrawer open={mrkOpen} onClose={() => setMrkOpen(false)} portfolioData={portfolio} orders={orders} />
    </>
  );
}
